import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Button from '../Button';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './Popup.css';
import { drawerDurationMs } from '../utils/cssDuration';

/**
 * Popup Component
 *
 * Desktop: centered modal popup (640px)
 * Mobile: bottom sheet drawer with swipe-to-dismiss
 *
 * Generic confirmation/action popup reusing DS components & tokens.
 *
 * Figma nodes:
 * - Parent:  3969:1982
 * - Desktop: 3969:1983
 * - Mobile:  3969:2047
 */

const Popup = ({
  isOpen = false,
  onClose,
  onConfirm,
  onCancel,
  title = 'Title',
  infoText = '',
  infoVariant = 'success',
  showInfo = true,
  message = '',
  showMessage = true,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  showCancel = true,
  confirmVariant = 'black',
  confirmDisabled = false,
  preview = false,
  lockScroll = true,
  className = '',
  children,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);

  const overlayRef = useRef(null);
  const enteredTimer = useRef(null);
  const closeTimer = useRef(null);

  /* ── Reset state when opened ── */
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setIsEntered(preview);

      if (!preview && lockScroll) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }

      if (!preview) {
        enteredTimer.current = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
      }
    } else if (!preview && lockScroll) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      if (!preview && lockScroll) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
      if (enteredTimer.current) clearTimeout(enteredTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [isOpen, preview, lockScroll]);

  /* ── Close helpers ── */
  const animateClose = useCallback(() => {
    setIsClosing(true);
    setIsEntered(false);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, drawerDurationMs());
  }, [onClose]);

  /* ── Mobile swipe-to-close ── */
  const {
    containerRef: mobileDrawerRef,
    dragOffset,
    isDragging,
    DragHandle,
  } = useSwipeToClose(animateClose, isOpen && !preview);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) animateClose();
  };

  const handleCancel = () => {
    onCancel?.();
    animateClose();
  };

  const handleConfirm = () => {
    onConfirm?.();
    animateClose();
  };

  /* ── Close on ESC ── */
  useEffect(() => {
    if (preview) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && isOpen) animateClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, animateClose, preview]);

  if (!isOpen && !isClosing) return null;

  const drawerStyle =
    dragOffset > 0 || isDragging
      ? {
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging
            ? 'none'
            : 'transform var(--transition-time-drawer) var(--transition-function-drawer)',
        }
      : {};

  /* ── Content shared between desktop & mobile ── */
  const renderContent = () => (
    <div className="ds-popup__container">
      {/* Close button */}
      <div className="ds-popup__close">
        <Button
          size="tertiary"
          variant="text"
          iconOnly
          iconLeft={<X size={20} />}
          onClick={animateClose}
          aria-label="Close"
        />
      </div>

      <div className="ds-popup__wrapper">
        {/* Top: title + info + message */}
        <div className="ds-popup__top">
          <h3 className="ds-popup__title typo-h3">{title}</h3>

          {showInfo && infoText && (
            <div className={`ds-popup__info ds-popup__info--${infoVariant}`}>
              <p className="ds-popup__info-text typo-body">{infoText}</p>
            </div>
          )}

          {showMessage && message && (
            <p className="ds-popup__message typo-body">{message}</p>
          )}

          {children}
        </div>

        {/* CTAs */}
        <div className="ds-popup__ctas">
          {showCancel && (
            <Button
              size="secondary"
              variant="gray"
              onClick={handleCancel}
              className="ds-popup__btn"
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            size="secondary"
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="ds-popup__btn"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className={`ds-popup__overlay ${isClosing ? 'ds-popup__overlay--closing' : ''} ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* ── Desktop: centered popup ── */}
      <div
        className={`ds-popup ds-popup--desktop ${isClosing ? 'ds-popup--closing' : ''} ${isEntered ? 'ds-popup--entered' : ''}`}
      >
        <div className="ds-popup__content">
          {renderContent()}
        </div>
      </div>

      {/* ── Mobile: bottom sheet ── */}
      <div
        ref={mobileDrawerRef}
        className={`ds-popup ds-popup--mobile ${isClosing ? 'ds-popup--closing' : ''} ${isEntered ? 'ds-popup--entered' : ''}`}
        style={drawerStyle}
      >
        <DragHandle className="ds-popup__swipe-handle" />
        <div className="ds-popup__content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

Popup.propTypes = {
  /** Whether the popup is open */
  isOpen: PropTypes.bool,
  /** Callback when the popup is closed (via X, overlay, ESC, cancel) */
  onClose: PropTypes.func,
  /** Callback when the confirm button is clicked */
  onConfirm: PropTypes.func,
  /** Callback when the cancel button is clicked (before close) */
  onCancel: PropTypes.func,
  /** Popup title */
  title: PropTypes.string,
  /** Info text displayed in the info tip box */
  infoText: PropTypes.string,
  /** Info box colours: success (green, default) / warning (amber) / error (red) — the ui-* token pairs */
  infoVariant: PropTypes.oneOf(['success', 'warning', 'error']),
  /** Whether to show the info tip box */
  showInfo: PropTypes.bool,
  /** Message body text */
  message: PropTypes.string,
  /** Whether to show the message */
  showMessage: PropTypes.bool,
  /** Cancel button label */
  cancelLabel: PropTypes.string,
  /** Confirm button label */
  confirmLabel: PropTypes.string,
  /** Whether to show the cancel button */
  showCancel: PropTypes.bool,
  /** Button variant for confirm: 'black' (default) or 'danger' for destructive actions */
  confirmVariant: PropTypes.oneOf(['black', 'danger', 'main', 'outline']),
  /** Whether the confirm button is disabled */
  confirmDisabled: PropTypes.bool,
  /** Static preview mode — disables scroll lock, auto-focus, ESC */
  preview: PropTypes.bool,
  /** Whether opening the popup locks page scroll */
  lockScroll: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Optional custom content below title/info/message (replaces or extends default body) */
  children: PropTypes.node,
};

export default Popup;
